import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Avatar, Box, Spinner } from '@chakra-ui/react';
import { getInitials } from '../../utils/helpers';

/**
 * User Avatar Component
 */
function UserAvatar({ 
    userProfile, 
    size = 'md', 
    showOnlineIndicator = false,
    className = '',
    ...props 
}) {
    const [profileImage, setProfileImage] = useState(() => {
        // Initialize with userProfile image or localStorage fallback
        const userProfileImage = userProfile?.profileImage;
        const localStorageImage = localStorage.getItem('userProfileImage');
        return userProfileImage || localStorageImage || null;
    });
    
    const [imageLoading, setImageLoading] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Memoize user email to prevent unnecessary effect triggers
    const userEmail = useMemo(() => userProfile?.email, [userProfile?.email]);

    /**
     * Effect to update profile image when userProfile changes
     */
    useEffect(() => {
        // Only update if userProfile has a profileImage that's different from current
        if (userProfile?.profileImage && userProfile.profileImage !== profileImage) {
            setProfileImage(userProfile.profileImage);
            setImageError(false);
        }
    }, [userProfile?.profileImage]); // Removed profileImage from dependencies to prevent loops

    /**
     * Effect to check localStorage when userProfile email is available
     */
    useEffect(() => {
        if (userEmail && !profileImage) {
            const localStorageImage = localStorage.getItem('userProfileImage');
            if (localStorageImage) {
                setProfileImage(localStorageImage);
                setImageError(false);
            }
        }
    }, [userEmail]); // Removed profileImage dependency to prevent loops

    /**
     * Listen for profile photo update events
     */
    const handleProfilePhotoUpdate = useCallback((event) => {
        const { email, profileImage: newProfileImage } = event.detail;
        
        // Only update if this avatar is for the same user
        if (email === userEmail) {
            setProfileImage(newProfileImage);
            setImageError(false);
            setImageLoading(false);
        }
    }, [userEmail]);

    useEffect(() => {
        if (userEmail) {
            window.addEventListener('userProfilePhotoUpdated', handleProfilePhotoUpdate);
            return () => {
                window.removeEventListener('userProfilePhotoUpdated', handleProfilePhotoUpdate);
            };
        }
    }, [userEmail, handleProfilePhotoUpdate]);

    /**
     * Image event handlers with useCallback
     */
    const handleImageLoadStart = useCallback(() => {
        setImageLoading(true);
        setImageError(false);
    }, []);

    const handleImageLoad = useCallback(() => {
        setImageLoading(false);
        setImageError(false);
    }, []);

    const handleImageError = useCallback(() => {
        setImageLoading(false);
        setImageError(true);
        setProfileImage(null);
    }, []);

    /**
     *Memoized calculations to prevent re-computation
     */
    const avatarSizes = useMemo(() => {
        const sizeMap = {
            '2xs': { size: '4', pixels: 16 },
            'xs': { size: '6', pixels: 24 },
            'sm': { size: '8', pixels: 32 },
            'md': { size: '12', pixels: 48 },
            'lg': { size: '16', pixels: 64 },
            'xl': { size: '24', pixels: 96 },
            '2xl': { size: '32', pixels: 128 }
        };
        return sizeMap[size] || sizeMap['md'];
    }, [size]);

    const userInitials = useMemo(() => {
        if (userProfile?.name) {
            return getInitials(userProfile.name);
        }
        if (userProfile?.email) {
            return getInitials(userProfile.email.split('@')[0]);
        }
        return 'U';
    }, [userProfile?.name, userProfile?.email]);

    const backgroundColor = useMemo(() => {
        if (!userProfile?.email) {
            return 'brand.500';
        }

        const colors = [
            'brand.500', 'blue.500', 'green.500', 'purple.500', 
            'pink.500', 'orange.500', 'teal.500', 'cyan.500'
        ];
        
        let hash = 0;
        for (let i = 0; i < userProfile.email.length; i++) {
            hash = userProfile.email.charCodeAt(i) + ((hash << 5) - hash);
        }
        
        return colors[Math.abs(hash) % colors.length];
    }, [userProfile?.email]);

    // Determine if we should show the image
    const shouldShowImage = !imageError && profileImage;

    return (
        <Box position="relative" className={className}>
            {/* Main Avatar */}
            <Avatar
                size={size}
                name={userInitials}
                src={shouldShowImage ? profileImage : undefined}
                bg={backgroundColor}
                color="white"
                fontWeight="semibold"
                onLoadStart={handleImageLoadStart}
                onLoad={handleImageLoad}
                onError={handleImageError}
                loading="lazy"
                {...props}
            />

            {/* Loading Spinner Overlay */}
            {imageLoading && profileImage && (
                <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="blackAlpha.200"
                    borderRadius="full"
                >
                    <Spinner
                        size={size === 'sm' ? 'xs' : 'sm'}
                        color="white"
                        thickness="2px"
                    />
                </Box>
            )}

            {/* Online Status Indicator */}
            {showOnlineIndicator && (
                <Box
                    position="absolute"
                    bottom="0"
                    right="0"
                    w={`${Math.max(avatarSizes.pixels * 0.25, 8)}px`}
                    h={`${Math.max(avatarSizes.pixels * 0.25, 8)}px`}
                    bg="green.400"
                    border="2px solid white"
                    borderRadius="full"
                    boxShadow="sm"
                />
            )}
        </Box>
    );
}

export default UserAvatar;